// The browser half of ForgeOps's own analytics (see app/services/analytics.rb). Events are queued
// and sent in small batches to /analytics/events, where the server checks each against its
// catalogue and drops anything it doesn't allow. Nothing here reads page content: only the
// event's name and a few short properties the caller names.
//
// Consent: the anonymous and session ids are cookies the consent banner sets only when the visitor
// agrees (analytics_consent_controller.js), and the server reads them from there. Without consent
// events still go, unlinked to one another. A browser that asks not to be tracked (Global Privacy
// Control or Do Not Track) sends nothing at all.
const ENDPOINT = "/analytics/events"
const queue = []
let timer = null

export function trackingAllowed() {
  return navigator.globalPrivacyControl !== true && navigator.doNotTrack !== "1"
}

function newId() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID()
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

export function track(eventName, properties = {}) {
  if (!trackingAllowed()) return

  queue.push({ event_id: newId(), event_name: eventName, occurred_at: new Date().toISOString(), properties })
  clearTimeout(timer)
  timer = setTimeout(flush, 400)
}

export function flush() {
  if (queue.length === 0) return

  const body = JSON.stringify({ events: queue.splice(0, 20) })
  const blob = new Blob([ body ], { type: "application/json" })
  if (!(navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob))) {
    fetch(ENDPOINT, { method: "POST", body, keepalive: true, headers: { "Content-Type": "application/json" } }).catch(() => {})
  }
  if (queue.length > 0) flush()
}

// Leaving the page sends whatever is still queued.
document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") flush() })

// Any link or button marked data-analytics-event="event_name", with data-analytics-props as a
// small JSON object, reports that event when it's clicked. Views mark the clicks worth counting.
document.addEventListener("click", (event) => {
  const marked = event.target.closest("[data-analytics-event]")
  if (!marked) return

  let properties = {}
  try { properties = JSON.parse(marked.dataset.analyticsProps || "{}") } catch { properties = {} }
  track(marked.dataset.analyticsEvent, properties)
})

// On an incident's or an issue's page, the links that open evidence: a trace, a slow query or
// dependency call (all trace pages), the release's errors, or the deploy's pull request or
// comparison. Counted as evidence_viewed or deployment_inspected; the trace page itself also
// counts trace_opened on the server.
const INVESTIGATION_PAGE = /\/organizations\/[^/]+\/projects\/[^/]+\/(incidents|issues)\/\d+\/?$/

document.addEventListener("click", (event) => {
  const link = event.target.closest("a[href]")
  if (!link || !INVESTIGATION_PAGE.test(location.pathname)) return

  const href = link.getAttribute("href")
  const text = link.textContent.toLowerCase()
  if (/\/pull\/|\/merge_requests\/|\/compare\//.test(href)) {
    track("deployment_inspected", { kind: /compare/.test(href) ? "compare" : "pull_request" })
  } else if (/\/traces(\/|\?|$)/.test(href)) {
    track("evidence_viewed", { evidence_type: text.includes("query") ? "query" : text.includes("dependency") || text.includes("call") ? "dependency" : "trace" })
  } else if (/[?&]release=/.test(href)) {
    track("evidence_viewed", { evidence_type: "release" })
  }
})

// A curated demo replay counts as completed when its conclusion comes into view.
const DEMO_REPLAY_PAGE = /^\/demo\/replay(\/(?!yours|shared)[a-z0-9-]+)?\/?$/

function watchDemoConclusion() {
  if (!DEMO_REPLAY_PAGE.test(location.pathname) || !("IntersectionObserver" in window)) return

  const conclusion = document.getElementById("replay-conclusion")
  if (!conclusion) return

  const observer = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      track("demo_completed", { demo_type: "replay", status: "reached_conclusion" })
      observer.disconnect()
    }
  })
  observer.observe(conclusion)
}

document.addEventListener("turbo:load", watchDemoConclusion)

window.ForgeOpsAnalytics = { track, flush }
